import React, { useState } from "react";
import { ChevronRight, Sparkles } from "lucide-react";

// Converts snake_case / camelCase / kebab-case keys into Title Case headers
const formatKey = (key) => {
  if (key === null || key === undefined) return "";
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isPrimitive = (value) =>
  value === null || (typeof value !== "object" && typeof value !== "function");

// Recursively determines whether a value carries no meaningful content, so
// empty fields can be omitted entirely.
const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.every(isEmpty);
  if (isPlainObject(value)) return Object.values(value).every(isEmpty);
  return false; // numbers / booleans are meaningful
};

const hasDateFormatPlaceholder = (text) => /MM\/DD\/YYYY/i.test(text);

// Removes just the "MM/DD/YYYY" token from a string (keeping the rest of the
// text) and tidies up any punctuation/whitespace left behind by its removal.
const removeDateFormatPlaceholder = (text) =>
  text
    .replace(/MM\/DD\/YYYY/gi, "")
    .replace(/\(\s*\)/g, "")
    .replace(/[,;:-]\s*(?=[,;:-]|$)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s,;:-]+|[\s,;:-]+$/g, "")
    .trim();

// Strips the "MM/DD/YYYY" placeholder out of any string found within a value
// (string, array, or plain object), leaving the surrounding content intact.
// If removing it empties a string entirely, that entry is dropped.
const stripDateFormatPlaceholder = (value) => {
  if (typeof value === "string") {
    if (!hasDateFormatPlaceholder(value)) return value;
    const cleaned = removeDateFormatPlaceholder(value);
    return cleaned === "" ? undefined : cleaned;
  }
  if (Array.isArray(value)) {
    return value
      .map(stripDateFormatPlaceholder)
      .filter((item) => item !== undefined);
  }
  if (isPlainObject(value)) {
    const cleaned = {};
    Object.entries(value).forEach(([key, val]) => {
      const result = stripDateFormatPlaceholder(val);
      if (result !== undefined) cleaned[key] = result;
    });
    return cleaned;
  }
  return value;
};

// The summary generator can occasionally serialize a nested field (e.g.
// section_specific) as a JSON-encoded string instead of a real object/array.
const looksLikeJson = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  );
};

// Recursively parses any JSON-encoded string values so double-encoded fields
// render as structured cards rather than raw JSON text.
const deepParseJsonStrings = (value) => {
  if (looksLikeJson(value)) {
    try {
      return deepParseJsonStrings(JSON.parse(value));
    } catch (error) {
      return value; // not valid JSON; leave the string untouched
    }
  }
  if (Array.isArray(value)) return value.map(deepParseJsonStrings);
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, deepParseJsonStrings(val)])
    );
  }
  return value;
};

const SHORT_STRING_MAX = 40;

// Collapses a section name down to a comparable token (lowercase, alphanumeric
// only) so keys like "chief_complaint" and "Chief Complaint" match.
const normalizeSectionKey = (value) =>
  String(value === null || value === undefined ? "" : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

// "sections_present" style array: list of objects describing a section
const isSectionList = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((item) => isPlainObject(item) && "section_type" in item);

// Known acronyms that should stay uppercase rather than being title-cased.
const ACRONYMS = new Set([
  "ros", "ecog", "hpi", "heent", "cns", "gi", "gu", "cv", "msk", "eomi",
  "perrla", "perla", "nad", "rrr", "wnl", "cva", "bp", "hr", "rr", "ekg",
  "ecg", "cbc", "bmp", "cmp", "mri", "ct", "pet", "copd", "chf", "ckd",
  "dm", "htn", "cad", "dvt", "pe", "uti", "gerd", "tsh", "psa", "inr",
  "abg", "loc", "dnr", "npo", "prn", "icd", "soap", "dob",
]);

const toTitleWord = (word) => {
  if (!word) return word;
  if (ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

// Like formatKey, but never all-caps: title-cases each word while keeping known
// acronyms (ROS, ECOG, ...) uppercase.
const formatLabel = (key) => {
  if (key === null || key === undefined) return "";
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map(toTitleWord)
    .join(" ");
};

// An array of short scalar tokens reads better inline (comma separated)
const isInlineArray = (value) => {
  if (!Array.isArray(value)) return false;
  const items = value.filter((item) => !isEmpty(item));
  return (
    items.length > 0 &&
    items.every(
      (item) =>
        typeof item !== "object" &&
        String(item).length <= SHORT_STRING_MAX &&
        !String(item).includes("\n")
    )
  );
};

const isInline = (value) => isPrimitive(value) || isInlineArray(value);

const renderInline = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => !isEmpty(item))
      .map((item) => String(item))
      .join(", ");
  }
  return String(value);
};

// Tokens the generator uses (inconsistently) to mean "no value". Only a value
// that is ENTIRELY one of these is treated as unknown; embedded notes such as
// "Outpatient visit (type unspecified)" stay as-is.
const UNKNOWN_TOKENS = new Set([
  "unknown",
  "n/a",
  "na",
  "none",
  "null",
  "tbd",
  "not specified",
  "unspecified",
  "not determined",
  "not provided",
  "not applicable",
  "not available",
]);

const isUnknownValue = (value) => {
  if (typeof value !== "string") return false;
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/^[<[({]+|[>\])}]+$/g, "")
    .trim();
  return UNKNOWN_TOKENS.has(cleaned);
};

// Uniform muted placeholder for unknown / unspecified values. Empty fields are
// still meaningful (their absence is information), so they're shown rather
// than silently omitted wherever this placeholder is used.
const UnknownTag = () => (
  <span className="text-gray-400 italic">Not specified in template</span>
);

// Renders a scalar value, swapping any "unknown" token (or a genuinely empty
// value) for the muted placeholder.
const PrimitiveText = ({ value, className }) =>
  isEmpty(value) || isUnknownValue(value) ? (
    <UnknownTag />
  ) : (
    <span className={className}>{String(value)}</span>
  );

const LabeledInline = ({ label, value }) => (
  <div className="text-sm leading-relaxed">
    <span className="font-semibold text-gray-800">{label}: </span>
    {isEmpty(value) || isUnknownValue(value) ? (
      <UnknownTag />
    ) : (
      <span className="text-gray-700 whitespace-pre-wrap">
        {renderInline(value)}
      </span>
    )}
  </div>
);

// Plain (non-bulleted) list of scalar items, matching the Sections Present look.
const PlainList = ({ items }) => (
  <ul className="space-y-1">
    {items
      .filter((item) => !isEmpty(item))
      .map((item, index) => (
        <li
          key={index}
          className="text-sm font-medium text-gray-800 leading-relaxed"
        >
          <PrimitiveText value={item} className="whitespace-pre-wrap" />
        </li>
      ))}
  </ul>
);

const BulletList = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">
    {items
      .filter((item) => !isEmpty(item))
      .map((item, index) => (
        <li key={index} className="text-sm text-gray-700 leading-relaxed">
          {isPrimitive(item) ? (
            <PrimitiveText value={item} className="whitespace-pre-wrap" />
          ) : (
            <NestedObject value={item} />
          )}
        </li>
      ))}
  </ul>
);

const SectionList = ({ items }) => (
  <ul className="space-y-1">
    {items.map((section, index) => {
      const jinja = section.jinja_var_name;
      const hasJinja = jinja && String(jinja).trim() !== "";
      return (
        <li key={index} className="text-sm text-gray-700 leading-relaxed">
          <span className="font-medium text-gray-800">{section.section_type}</span>
          {hasJinja && <span className="text-gray-500"> ({jinja})</span>}
        </li>
      );
    })}
  </ul>
);

// A nested object rendered as a click-to-expand toggle with a purple header.
const CollapsibleBlock = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-gray-100 bg-gray-50">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-left"
      >
        <ChevronRight
          className={`h-4 w-4 text-ktx-purple shrink-0 transition-transform ${
            open ? "rotate-90" : ""
          }`}
        />
        <span className="text-sm font-bold text-gray-900">{title}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
};

// Renders the non-empty fields of an object. Scalars/short arrays render inline;
// arrays become bullet lists; nested objects become collapsible sub-modules.
// `labelResolver` (when provided) maps a child key to a preferred display label
// for this object's direct children only (used to match Sections Present casing).
const NestedObject = ({ value, labelResolver, sourceResolver }) => {
  const entries = Object.entries(value).filter(([, val]) => !isEmpty(val));
  if (entries.length === 0) return null;

  return (
    <div className="space-y-3">
      {entries.map(([key, val]) => {
        const baseLabel =
          (labelResolver && labelResolver(key)) || formatKey(key);
        // When a source resolver is supplied (the merged "Sections Present"
        // card), show the section's jinja source in muted grey next to its
        // name, matching the old Sections Present styling.
        const source = sourceResolver ? sourceResolver(key) : null;
        const label =
          source && String(source).trim() !== "" ? (
            <>
              {baseLabel}
              <span className="font-normal text-gray-500"> ({source})</span>
            </>
          ) : (
            baseLabel
          );

        // Only true scalars render inline; arrays always become (non-bold)
        // bulleted blocks so a short single-item list doesn't appear bold.
        if (isPrimitive(val)) {
          return <LabeledInline key={key} label={label} value={val} />;
        }

        if (isSectionList(val)) {
          return (
            <div key={key} className="space-y-1.5">
              <div className="text-sm font-medium text-gray-600">{label}</div>
              <SectionList items={val} />
            </div>
          );
        }

        if (Array.isArray(val)) {
          return (
            <div key={key} className="space-y-1.5">
              <div className="text-sm font-medium text-gray-600">{label}</div>
              <BulletList items={val} />
            </div>
          );
        }

        // Nested object -> collapsible toggle (inner levels use default labels)
        return (
          <CollapsibleBlock key={key} title={label}>
            <NestedObject value={val} />
          </CollapsibleBlock>
        );
      })}
    </div>
  );
};

// One top-level section rendered as a self-contained card with a header bar.
const SummaryCard = ({ title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
    <div className="px-4 py-2 border-b border-gray-200 bg-gray-100 text-sm font-bold text-gray-900 flex items-center gap-1.5">
      {title}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const SectionBody = ({ value, labelResolver, sourceResolver }) => {
  if (isSectionList(value)) return <SectionList items={value} />;
  if (Array.isArray(value)) {
    const items = value.filter((item) => !isEmpty(item));
    return items.every(isPrimitive) ? (
      <PlainList items={items} />
    ) : (
      <BulletList items={items} />
    );
  }
  if (isPlainObject(value))
    return (
      <NestedObject
        value={value}
        labelResolver={labelResolver}
        sourceResolver={sourceResolver}
      />
    );
  return (
    <PrimitiveText
      value={value}
      className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
    />
  );
};

const SummaryRenderer = ({ value, patientContextOnly = false }) => {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      parsed = value;
    }
  }

  // Normalize any double-encoded (stringified) nested fields before rendering.
  parsed = deepParseJsonStrings(parsed);

  if (!isPlainObject(parsed)) {
    return (
      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
        {String(parsed)}
      </p>
    );
  }

  const entries = Object.entries(parsed);

  // Pull the formatting fields out of their own section: date_formatting goes to
  // Overview, reference is folded into the Critical Rules card.
  const formatting = parsed.formatting_throughout_the_note;
  const dateFormatting = isPlainObject(formatting) ? formatting.date_formatting : null;
  const reference = isPlainObject(formatting) ? formatting.reference : null;

  const specialty = parsed.specialty ?? null;
  const visitType = parsed.visit_type ?? null;
  const sources = parsed.sources ?? null;

  // Build a lookup from section name -> jinja source using sections_present, so
  // the merged "Sections Present" card can show each section's source in grey.
  const sourceMap = {};
  if (Array.isArray(parsed.sections_present)) {
    parsed.sections_present.forEach((section) => {
      if (!isPlainObject(section)) return;
      const key = normalizeSectionKey(section.section_type);
      const src = section.jinja_var_name;
      if (key && src && String(src).trim() !== "") {
        sourceMap[key] = String(src).trim();
      }
    });
  }
  const sectionSourceResolver = (key) => sourceMap[normalizeSectionKey(key)];

  const overview = [];
  const sections = [];
  let patientContext = null; // [key, val] rendered full-width at the bottom
  let referencePlaced = false;

  entries.forEach(([key, val]) => {
    if (key === "formatting_throughout_the_note") {
      return; // handled explicitly above
    }
    if (key === "sections_present") {
      return; // merged into the renamed "Sections Present" (section_specific) card
    }
    if (key === "specialty" || key === "visit_type" || key === "sources") {
      return; // always-present Overview fields, handled explicitly below
    }
    if (key === "patient_context_pulled") {
      patientContext = [key, val];
      return;
    }
    if (key === "critical_rules") {
      const cleanedVal =
        isPlainObject(val) && "medical_abbreviations" in val
          ? {
              ...val,
              medical_abbreviations: stripDateFormatPlaceholder(
                val.medical_abbreviations
              ),
            }
          : val;
      let augmented = cleanedVal;
      if (!isEmpty(reference) && isPlainObject(cleanedVal)) {
        // Insert reference directly beneath the medical abbreviations field.
        augmented = {};
        let inserted = false;
        Object.entries(cleanedVal).forEach(([childKey, childVal]) => {
          augmented[childKey] = childVal;
          if (childKey === "medical_abbreviations") {
            augmented.reference = reference;
            inserted = true;
          }
        });
        if (!inserted) augmented.reference = reference;
        referencePlaced = true;
      }
      if (!isEmpty(augmented)) sections.push([key, augmented]);
      return;
    }
    if (isInline(val)) overview.push([key, val]);
    else if (!isEmpty(val)) sections.push([key, val]);
  });

  // Always show these four Overview fields, even when blank/missing.
  overview.push(["specialty", specialty]);
  overview.push(["visit_type", visitType]);
  overview.push(["sources", sources]);
  overview.push(["date_formatting", dateFormatting]);
  // If there was no critical_rules section, still surface the reference field.
  if (!isEmpty(reference) && !referencePlaced) {
    sections.push(["critical_rules", { reference }]);
  }
  // If section_specific is absent, fall back to showing sections_present so the
  // "Sections Present" card still appears.
  const hasSectionSpecific = sections.some(([key]) => key === "section_specific");
  if (!hasSectionSpecific && !isEmpty(parsed.sections_present)) {
    sections.push(["sections_present", parsed.sections_present]);
  }
  // Patient context is placed last so it comes after Sections Present / Critical
  // Rules, while still flowing inside the two-column layout.
  if (patientContext && !isEmpty(patientContext[1])) {
    sections.push(patientContext);
  }

  const renderSectionCard = ([key, val]) => {
    // section_specific is displayed as the merged "Sections Present" card; when
    // it is missing we render sections_present under the same title.
    const isMergedSections =
      key === "section_specific" || key === "sections_present";
    const title = isMergedSections ? (
      "Sections Present"
    ) : key === "patient_context_pulled" ? (
      <>
        {formatKey(key)}{" "}
        <span className="font-normal text-gray-500">(jinja vars)</span>
      </>
    ) : (
      formatKey(key)
    );
    // Patient context is pulled from jinja vars (not AI-generated), so it omits
    // the Sparkles marker used on the other cards.
    const titleWithIcon =
      key === "patient_context_pulled" ? (
        title
      ) : (
        <>
          <Sparkles
            className="h-4 w-4 text-ktx-purple/95 shrink-0"
            aria-label="AI-generated"
          >
            <title>AI-generated</title>
          </Sparkles>
          {title}
        </>
      );
    return (
      <SummaryCard key={key} title={titleWithIcon}>
        <SectionBody
          value={val}
          labelResolver={key === "section_specific" ? formatLabel : undefined}
          sourceResolver={
            key === "section_specific" ? sectionSourceResolver : undefined
          }
        />
      </SummaryCard>
    );
  };

  // SOAP note templates only surface the "patient context pulled" section; the
  // remaining summary cards (Overview, Sections Present, Critical Rules, ...)
  // are intentionally hidden for this view.
  if (patientContextOnly) {
    if (!patientContext || isEmpty(patientContext[1])) {
      return (
        <p className="text-sm text-gray-500 italic leading-relaxed">
          No patient context pulled for this template.
        </p>
      );
    }
    return <div className="space-y-4">{renderSectionCard(patientContext)}</div>;
  }

  return (
    <div className="space-y-4">
      {overview.length > 0 && (
        <SummaryCard title="Overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {overview.map(([key, val]) => (
              <div key={key}>
                <LabeledInline
                  label={
                    key === "specialty" ? "Specialty/Role" : formatKey(key)
                  }
                  value={val}
                />
              </div>
            ))}
          </div>
        </SummaryCard>
      )}

      {sections.length > 0 && (
        // Two independent columns (masonry-style) so a card can rise to fill the
        // gap left by a short neighbor instead of being held down by grid rows.
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="flex flex-col gap-4 flex-1 min-w-0 w-full">
            {sections
              .filter((_, index) => index % 2 === 0)
              .map(renderSectionCard)}
          </div>
          <div className="flex flex-col gap-4 flex-1 min-w-0 w-full">
            {sections
              .filter((_, index) => index % 2 === 1)
              .map(renderSectionCard)}
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryRenderer;
